import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, FormField, FormHelp, Label, Textarea } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertyEditV2WebApiHandler_leaseTerms } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../hooks/queries/multiplex';
import { useGetListingDetails, useGetListingRecommendations } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { getFirstUnitError } from '../../../utils/multiplexUpdateErrorUtils';
import propertyUtils from '../../../utils/propertyUtils';
import { getRoomForRentFlow } from '../../../utils/roomForRentUtils';

import { RECS } from '../../../constants/RecommendationsConstants';

import { FlowType } from '../../CreateListingPage/types';
import LocaleNotice from '../../LocaleNotice';
import RecommendationLabel from '../../RecommendationLabel';

const inputName = 'leaseTerms';

const basePlaceholder = `Example: Owner pays for water. Renter is responsible for gas and electric. Last month’s rent due at signing. No smoking allowed. Up to three pets total allowed.`;
const roomForRentPlaceholder = 'Example: No smoking allowed inside';

const LeaseTermsFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const placeholder = isRoomForRent ? roomForRentPlaceholder : basePlaceholder;

  const recommendationTypes = new Set(recommendationsData?.recommendations?.map((r) => r.type) ?? []);

  const initialValue =
    !isOnReviewStep && multiplexData?.leaseTerms ? multiplexData.leaseTerms : (listingDetails?.leaseTerms ?? '');
  const displayLocalNotice = getRoomForRentFlow(listingDetails?.forRentByType) !== FlowType.ROOM_FOR_RENT_RYAN;

  const [multiplexError, setMultiplexError] = useState<string | undefined>(undefined);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_leaseTerms,
    applyToCache: true,
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_leaseTerms' } },
  });

  const field = useFieldSave({
    fieldName: inputName,
    initialValue,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { listingAlias, leaseTerms: value },
    }),
  });

  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'LeaseTermsFormField',
        fieldName: inputName,
      },
      invalidates: ['listingDetails', 'listingRecommendations'],
    },
  });

  const handleBlur = async () => {
    setMultiplexError(undefined);

    if (isOnReviewStep || !isInMultiplexListingFlow) {
      field.onBlur?.();
      return;
    }

    if (!multiplexBuildingSessionId) {
      /**
       * Defensive guard only: multiplex save runs when `isInMultiplexListingFlow` is true, which should
       * already imply a session id. `useMultiplexUpdate` also rejects empty ids before any request.
       */
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: { details: 'LeaseTermsFormField.handleBlur', fieldName: inputName },
      });
      return;
    }

    try {
      const response = await multiplexUpdateAsync({
        postData: { multiplexBuildingSessionId, leaseTerms: field.value },
      });

      const unitErrorMessage = getFirstUnitError(response.units, {
        fieldName: inputName,
        fallbackMessage: 'Lease terms couldn’t be saved. Reload the page and try again.',
        responseFailed: response.success === false,
      });

      if (unitErrorMessage) {
        setMultiplexError(unitErrorMessage);
      }
    } catch (err) {
      const message = DatarouterError.is(err)
        ? DatarouterError.getDisplayMessage(err)
        : ((err as { message?: string })?.message ?? '');
      setMultiplexError(message);
    }
  };

  const displayError = multiplexError ?? field.error;

  return (
    <>
      <FormField
        data-testid="LeaseTermsFormField"
        label={
          isRelistingFieldset && recommendationTypes.has(RECS.LEASE_TERMS) ? (
            <RecommendationLabel label="Lease terms" recommendationType={RECS.LEASE_TERMS} />
          ) : (
            <Label>Lease terms</Label>
          )
        }
        error={!!displayError}
        description={displayError ? <FormHelp>{displayError}</FormHelp> : undefined}
      >
        <Textarea
          name={inputName}
          value={field.value}
          placeholder={placeholder}
          onChange={(e) => field.onChange(e.target.value)}
          onBlur={handleBlur}
          rows={10}
        />
      </FormField>
      {displayLocalNotice && listingDetails?.localeSpecificNotice && (
        <Box css={{ marginBlock: '400' }}>
          <LocaleNotice localeSpecificNotice={listingDetails.localeSpecificNotice} />
        </Box>
      )}
    </>
  );
};

export default LeaseTermsFormField;
